import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  forwardRef,
  SimpleChanges,
  SimpleChange,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { LoggerService } from "@bfx/logger";
import { ICTenorValueDateModel } from "app/order-entry/interface/controls/models/bfx-interface-controls.models";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
const logger = LoggerService.for(__dirname);

import * as CubiqTS from "shared/cubiq-model-multi-asset";

export const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAutoFwdPricingComponent),
  multi: true,
};

/**
 * Form component for the Auto Forward Roll checkbox on the More panel. It uses the client and
 * value date to determine its default value and enabled state (see story )
 */
@Component({
  selector: "ic-auto-fwd-pricing",
  templateUrl: "./html/ic-auto-fwd-pricing.html",
  providers: [VALUE_ACCESSOR],
  standalone: false,
})
export class ICAutoFwdPricingComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  /**********************************************************************/
  /* INPUT */

  @Input() inputId: string;

  /** Track client changes to always reset to client default regardless of local changes */
  @Input() clientId: string;

  @Input() clientAutoFwdType: CubiqTS.ForwardPricingType;

  @Input() valueDate: ICTenorValueDateModel;

  @Input() product: CubiqTS.Product;

  @Input() enabledConfig: boolean;

  @Input() manualOnly: boolean;

  @Input() label: any = "Auto Forward Roll";

  @Input() isLoading: boolean = true;

  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  /**********************************************************************/
  /* DATA */

  public data: any = {
    label: "Auto Forward Roll",
    enabled: false,
    value: null,
    locked: false,
  };

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor() {}

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  ngOnChanges(changes: SimpleChanges) {
    this.onConstraintChanges(changes);
  }

  /**********************************************************************/
  /* INITIALIZATION */
  public initialize(): void {
    this.data.label = this.label;
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  public writeValue(value: any): void {
    this.data.value = value;
  }

  public registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  public registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* BASIC - API */

  public load(value: boolean): void {
    if (typeof value === "boolean" || value === null) {
      this.data.value = value;
    } else {
      logger.out.error(`ERROR: IcAutoForwardPricing.load(): failed to load '${value}'`);
    }
  }

  public reset(): void {
    this.data.value = null;
    this.data.locked = false;
  }

  public lock(): void {
    this.data.locked = true;
  }

  public unlock(): void {
    this.data.locked = false;
  }
  /**********************************************************************/
  /* ON CHANGES */

  public onConstraintChanges(changes: SimpleChanges): void {
    let updateSettings: boolean = false;
    let reset: boolean = false;
    for (let propName in changes) {
      const change: SimpleChange = changes[propName];
      switch (propName) {
        case "clientId":
          updateSettings = true;
          reset = true;
          break;
        case "product":
          updateSettings = true;
          reset =
            change.previousValue === CubiqTS.Product.SPT &&
            change.currentValue !== CubiqTS.Product.SPT;
          break;
        case "valueDate":
          // Don't want to change value fwd tenor changes from one tenor to another
          if (
            !change.previousValue ||
            !change.currentValue ||
            change.previousValue.tenor == CubiqTS.Tenor.SPOT ||
            change.currentValue.tenor == CubiqTS.Tenor.SPOT
          ) {
            updateSettings = true;
          }
          break;
      }
    }

    let simpleSpot =
      (this.valueDate == null || this.valueDate.tenor == CubiqTS.Tenor.SPOT) &&
      (null == this.product || this.product == CubiqTS.Product.SPT);

    const fwdSupported = !(this.manualOnly || simpleSpot || this.clientAutoFwdType === null);
    const justEnabled = !this.data.enabled && this.enabledConfig && fwdSupported;

    this.data.enabled = this.enabledConfig && fwdSupported;

    if (updateSettings) {
      const oldValue = this.data.value;

      if (!fwdSupported) {
        if (!this.isLoading) {
          this.data.value = false;
        }
      } else {
        if (!this.isLoading) {
          if (reset && this.enabledConfig) {
            // Changing a client or product will always revert the fwd pricing flag
            this.data.value = this.clientAutoFwdType === CubiqTS.ForwardPricingType.AUTO;
          } else {
            // Unless the disabled state of the control has changed the value is not changed by
            // a change in setting. If it's just become enabled, then it takes the Clienty default value
            this.data.value =
              (oldValue && this.enabledConfig) ||
              (justEnabled && this.clientAutoFwdType === CubiqTS.ForwardPricingType.AUTO);
          }
        }
      }
      if (this.data.value != oldValue) {
        this.onModelChange(this.data.value);
      }
    }
  }

  public onValueChange(): void {
    this.onModelChange(this.data.value);
  }
}
